import { IS_CHROME_EXTENSION } from "@src/env";
import { useEffect, useRef, useState } from "react";
import type { ChromeExtensionMessage } from "../../types/chrome-extension";
import { PubSub } from "../../utils/pubsub";

export const chromeExtensionPubSub = new PubSub<ChromeExtensionMessage>();

/**
 * Hook to listen for messages from the Chrome extension background script.
 * Establishes a port connection and handles message routing.
 * Each panel instance is associated with its own tab ID.
 */
export function useBackgroundMessages() {
  const [isConnected, setIsConnected] = useState(false);
  const [tabId, setTabId] = useState<number | null>(null);
  const portRef = useRef<chrome.runtime.Port | null>(null);

  useEffect(() => {
    if (!IS_CHROME_EXTENSION) {
      console.log("[useBackgroundMessages] Not in Chrome extension context");
      return;
    }

    if (typeof chrome === "undefined" || !chrome.runtime?.connect) {
      console.log("[useBackgroundMessages] Not in Chrome extension context");
      return;
    }

    const port = chrome.runtime.connect(undefined, { name: "sidepanel" });
    portRef.current = port;

    // Get the current tab ID and send it with the panelReady message
    // Side panels can use chrome.tabs.query to find the active tab in the current window
    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      const currentTabId = tabs[0]?.id;

      if (currentTabId) {
        setTabId(currentTabId);
        setIsConnected(true);

        console.log(
          `[useBackgroundMessages] Connected to background script for tab ${currentTabId}`,
        );

        // Send tabId along with panelReady so background knows which tab this panel belongs to
        port.postMessage({ type: "panelReady", tabId: currentTabId });
      } else {
        console.error(
          "[useBackgroundMessages] Could not determine current tab ID",
        );
      }
    });

    const handleMessage = (message: ChromeExtensionMessage) => {
      chromeExtensionPubSub.publish(message);
    };

    port.onMessage.addListener(handleMessage);

    port.onDisconnect.addListener(() => {
      console.log(
        "[useBackgroundMessages] Disconnected from background script",
      );
      setIsConnected(false);
      setTabId(null);
      portRef.current = null;
    });

    return () => {
      port.disconnect();
      portRef.current = null;
      setIsConnected(false);
      setTabId(null);
      chromeExtensionPubSub.clear();
    };
  }, []);

  return {
    isConnected,
    tabId,
  };
}
